import React, { useState, useEffect } from 'react';

/**
 * ConfidenceDisplay component shows the confidence percentage with a subtle animation
 * when the value changes
 * 
 * @param {number} confidenceScore - The confidence score (0-100)
 * @param {boolean} visible - Whether the component should be visible
 */
const ConfidenceDisplay = ({ confidenceScore = 0, visible = false }) => {
  const [displayedScore, setDisplayedScore] = useState(() => {
    // Initialize from localStorage if available
    const cachedScore = localStorage.getItem('pokket_confidence_score');
    return cachedScore ? parseInt(cachedScore, 10) : confidenceScore;
  });
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Update the displayed score with animation when confidence changes
  useEffect(() => {
    // Only update if confidence score changes and is valid
    if (confidenceScore !== displayedScore && confidenceScore > 0) {
      setIsAnimating(true);
      
      // Cache the confidence score in localStorage
      localStorage.setItem('pokket_confidence_score', confidenceScore.toString());
      
      // Short timeout to allow animation to complete
      const animationTimeout = setTimeout(() => {
        setDisplayedScore(confidenceScore);
        
        // Reset animation state after a delay
        setTimeout(() => {
          setIsAnimating(false);
        }, 300);
      }, 300);
      
      return () => clearTimeout(animationTimeout);
    }
  }, [confidenceScore, displayedScore]);
  
  // Don't render if confidence is >= 100 or component not visible
  if (!visible || displayedScore >= 100) {
    return null;
  }
  
  const roundedConfidence = Math.round(displayedScore);
  
  return (
    <div className="w-full text-center my-2">
      <p 
        className={`text-sm text-gray-400 transition-all duration-300 ${
          isAnimating ? 'transform scale-110 text-blue-400' : ''
        }`}
      >
        {displayedScore < 20 
          ? "(Pokket needs to know a bit more before they can give advice.)"
          : `(Pokket is ${roundedConfidence}% ready to give advice.)`
        }
      </p>
    </div>
  );
};

export default ConfidenceDisplay; 